"use client";

import AddQuantity from "@/components/common/AddQuantity";
import ProductHighlights from "./ProductHighlights";
import ProductReviews from "./ProductReviews";
import ProductImageCarousel from "./ProductImageCarousel";
import { useEffect, useState } from "react";
import { useSearchParams, useRouter } from "next/navigation";
import useCommonStore from "@/store/commonStore";
import { toast } from "@/hooks/use-toast";
import useCartStore from "@/store/cartStore";
import useOrderStore from "@/store/orderStore";
import useProductStore from "@/store/productStore";
import CommonHeader from '@/components/common/CommonHeader'
import Image from "next/image";
import { GTMAddToCartHandler, GTMRemoveFromCartHandler, GTMUpdateOrderSummaryHandler, GTMViewItemHandler } from "@/lib/analytics";

interface IObj {
  [key: string]: any;
}
interface ICartItem {
  pdt_id: string;
  variant_id: string;
  quantity: number;
}

export default function ProductInfo({ details }: { details: IObj }) {
  const { isAuth, translations } = useCommonStore();
  const searchParams = useSearchParams();
  const router = useRouter();
  const [variant, setVariant] = useState<IObj>({});
  const cart = useCartStore((state) => state.cart);
  const updateCart = useCartStore((state) => state.updateCart);
  const { updateUserOrderSummary } = useOrderStore();
  const { productReviews, fetchReviews } = useProductStore();

  useEffect(() => {
    const variantId = searchParams.get("variant_id");
    const selected =
      details?.variants?.find((v: IObj) => v.variant_id === variantId) ||
      details?.variants?.[0];
    if (selected && selected.variant_id !== variant.variant_id) {
      changeVariant(selected);
      const match = cart?.find((i) =>
        variant?.variant_id
          ? i.pdt_id === details?.pdt_id &&
          i.variant_id === variant?.variant_id
          : i.pdt_id === details?.pdt_id
      );
      const cartQnty = match?.quantity || 0;


      GTMViewItemHandler({
        curr: details.currency,
        value: selected.spl_prc_in_num,
        item: {
          item_id: selected?.pdt_id,
          item_variant: selected?.variant_id,
          item_name: details?.pdt_nm,
          item_brand: details?.brnd_nm,
          item_category: selected?.category || "",
          price: selected.spl_prc_in_num,
          quantity: cartQnty,
        },
      })
    } else {
      const match = cart?.find((i) =>
        variant?.variant_id
          ? i.pdt_id === details?.pdt_id &&
          i.variant_id === variant?.variant_id
          : i.pdt_id === details?.pdt_id
      );
      const cartQnty = match?.quantity || 0;
      GTMViewItemHandler({
        curr: details.currency,
        value: details.spl_prc_in_num,
        item: {
          item_id: details.pdt_id,
          item_name: details.pdt_nm,
          item_brand: details.brnd_nm,
          item_category: details?.category || "",
          price: details.spl_prc_in_num,
          quantity: cartQnty,
        },
      })
    }
  }, [searchParams.get("variant_id"), details?.pdt_id, cart]);

  useEffect(() => {
    const prodId = details.pdt_id;
    fetchReviews(prodId);
  }, [details.pdt_id]);
  const changeVariant = (v: IObj) => {
    if (v.variant_id === variant.variant_id) return; // Prevent unnecessary updates
    
    const params = new URLSearchParams(searchParams.toString());
    params.set("variant_id", v.variant_id);
    router.replace(`?${params.toString()}`, { scroll: false });
    setVariant(v);
    // const match = cart.find(i => i.pdt_id === details?.pdt_id && i.variant_id === v.variant_id);
    // setVariant(prev => ({ ...prev, cart_qnty: match?.quantity || 0 }));
  };

  // useEffect(() => {
  //   const match = cart.find(i => i.pdt_id === details?.pdt_id && i.variant_id === variant?.variant_id);
  //   setVariant(prev => ({ ...prev, cart_qnty: match?.quantity || 0 }));
  // }, [cart])

  const cartHandler = async (item: ICartItem, type?: String, spl_prc_in_num?: number, pdt_nm?: string) => {
    if (details?.variants?.length > 0 && !item.variant_id) {
      toast({ variant: "destructive", title: "Please select a variant" });
      return;
    }
    if (type === "increment") {
      GTMAddToCartHandler({
        curr: details.currency,
        value: spl_prc_in_num,
        items: [{
          item_id: item.pdt_id,
          item_variant: item?.variant_id,
          item_name: pdt_nm,
          price: spl_prc_in_num,
          quantity: item.quantity
        }]

      });
    } else if (type === "decrease") {
      GTMRemoveFromCartHandler({
        curr: details?.currency,
        value: spl_prc_in_num,
        items: [{
          item_id: item.pdt_id,
          item_variant: item?.variant_id,
          item_name: pdt_nm,
          price: spl_prc_in_num,
          quantity: item.quantity
        }]
      });

    }
    const update = await updateCart(item, details?.max_order_quantity, isAuth);
    if (update?.status) {
      toast({
        variant: "default",
        title: "Cart updated",
        description: update?.message || "",
      });
    } else {
      toast({
        variant: "destructive",
        title: update?.message,
        description: update?.error,
      });
    }
  };
  const updateOrderSummary = async (item: { [key: string]: any },spl_prc_in_num:number) => {

    GTMUpdateOrderSummaryHandler({
      items: [{
        item_id: item.pdt_id,
        item_variant: item?.variant_id,
        quantity: item.quantity,
        price:spl_prc_in_num
      }]
    })
    const result = await updateUserOrderSummary({ items: [item] });
    if (result?.status) {
      router.push("/checkout");
    } else {
      toast({
        title: "Error",
        description: result?.message || result?.error,
        variant: "destructive",
      });
    }
  };
  let spl_prc = variant?.spl_prc ? variant?.spl_prc : details?.spl_prc;
  let spl_prc_in_num = variant?.spl_prc_in_num ? variant?.spl_prc_in_num : details?.spl_prc_in_num;
  let prc = variant?.price ? variant?.price : details?.price;
  let disc = variant?.disc ? variant?.disc : details?.disc;
  return (
    <>
      <button
        onClick={() => router.back()}
        aria-label="Back"
        className="hidden md:flex gap-2 mb-5"
      >
        <Image src={"/back-arrow.svg"} alt="back" width={18} height={18} />
        {translations["back"] || "Back"}
      </button>
      <CommonHeader title='prod_details' style='fixed top-0 left-0 right-0 z-10 shadow-sm' />
      <div className="flex flex-col md:flex-row gap-4 md:gap-8 py-[80px] md:py-0">
        <div className="flex-1 flex flex-col">
          <ProductImageCarousel
            details={details}
            disc={variant?.disc ? variant?.disc : details?.disc}
            images={
              variant?.image?.length
                ? [...variant?.image, ...details?.image]
                : details?.image
            }
          />
        </div>
        <div className="flex-1 flex flex-col gap-4">
          <div className="md:w-full flex flex-col gap-4 border border-chart-2 rounded-[10px] p-3 md:p-6 shadow-[0px_4px_30px_0px_#0000000F]">
            <h2 className="text-xs text-gray-600 font-bold">
              {details?.brnd_nm}
            </h2>
            <div className="mb-1 flex items-start justify-between">
              <p className="text-md md:text-xl font-semibold flex-1">
                {details?.pdt_nm}
              </p>
              {/* <div className="flex gap-3 ml-4">
                <button className="text-primary p-2 w-8 h-8 flex items-center justify-center rounded text-sm font-medium bg-[#FFFCF0]"><Share2 /></button>
                <button className="text-primary p-2 w-8 h-8 flex items-center justify-center rounded text-sm font-medium bg-[#FFFCF0]"><MessageCircle /></button>
              </div> */}
            </div>
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 md:gap-4">
                <span className="flex flex-col items-center">
                  <span className="text-xl md:text-2xl font-bold text-[#222]">
                    {spl_prc}
                  </span>
                  {prc != spl_prc && (
                    <span className="text-sm md:text-base font-semibold text-gray-500 line-through">
                      {prc}
                    </span>
                  )}{" "}
                </span>
                {/* <span className="text-green-600 font-semibold text-sm">({variant?.disc ? variant?.disc : details?.disc})</span> */}
                {disc && prc != spl_prc && (
                  <div
                    className="bg-[#00A25E] text-white font-extrabold text-xs md:text-sm px-3 py-1 flex items-center justify-center rounded-sm"
                    style={{
                      clipPath:
                        "polygon(10% 0, 100% 0%, 100% 100%, 10% 100%, 0% 50%)",
                    }}
                  >
                    {disc} {translations["off"] || "OFF"}
                  </div>
                )}
              </div>
              {/* <div className="flex gap-2"> */}
              {(() => {
                const match = cart?.find((i) =>
                  variant?.variant_id
                    ? i.pdt_id === details?.pdt_id &&
                    i.variant_id === variant?.variant_id
                    : i.pdt_id === details?.pdt_id
                );
                const cartQnty = match?.quantity || 0;
                return (
                  variant?.hasOwnProperty("in_stk")
                    ? variant.in_stk
                    : details?.in_stk
                ) ? (
                  <AddQuantity
                    quantity={cartQnty}
                    onAdd={() =>
                      cartHandler({
                        pdt_id: details?.pdt_id,
                        variant_id: variant?.variant_id || "",
                        quantity: cartQnty + 1,
                      }, "increment", spl_prc_in_num, details?.pdt_nm)
                    }
                    onRemove={() =>
                      cartHandler({
                        pdt_id: details?.pdt_id,
                        variant_id: variant?.variant_id || "",
                        quantity: cartQnty - 1,
                      }, `decrease`, spl_prc_in_num, details?.pdt_nm)
                    }
                  />
                ) : (
                  <span className="bg-red-200 p-2 py-1.5 rounded-lg text-sm font-bold text-red-700">
                    {translations["out_of_stock"] || "Out of Stock"}
                  </span>
                );
              })()}
              {/* </div> */}
            </div>
            {/* <div className="flex gap-2">
              {
                details?.tags?.map((tag: IObj, idx: number) => (
                  <span style={{ backgroundColor: tag?.bg_clr || "#000", color: tag?.txt_clr || "#fff" }} key={idx} className={`text-xs font-semibold px-1 py-[2px] rounded`}>{tag?.tag_labl}</span>
                ))
              }
            </div> */}
            {details?.variants?.length > 0 && (
              <div className="mb-2">
                <div className="font-semibold text-sm mb-2 mt-2">
                  {translations["variants"] || "Variants"} (
                  {details?.variants?.length})
                </div>
                <div className="flex gap-4 flex-wrap ">
                  {details?.variants?.map((v: IObj, idx: number) => (
                    <button
                      key={idx}
                      onClick={() => changeVariant(v)}
                      className={`border p-2 text-sm font-medium rounded-[10px] ${
                        v.variant_id === searchParams.get("variant_id")
                          ? "bg-[#FFF8EC] text-primary"
                          : "text-gray-600"
                        }`}
                    >
                      <img
                        src={
                          v?.thumbnail
                            ? v?.thumbnail
                            : v?.image[0]
                              ? v?.image[0]
                              : details?.thumbnail
                        }
                        alt="color"
                        className="inline w-6 h-6"
                      />
                      <p className="text-[.7rem] mt-1 font-semibold">
                        {v?.size}
                      </p>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <ProductHighlights
              translations={translations}
              highlights={
                variant?.highlights ? variant?.highlights : details?.highlights
              }
            />
            {/* <div className="flex gap-3 flex-wrap mb-2 stick sticky bottom-0 z-5 p-3 bg-white shadow-[0_-4px_8px_-4px_rgba(0,0,0,0.2)] ">
            <button className=" flex-1 border px-4 py-3 rounded font-medium text-sm hover:bg-gray-50 bg-white">Buy now</button>
          </div> */}
          </div>
          <div className="flex flex-col gap-3">
            {details?.desc && (
              <h3 className="font-bold">
                {translations["description"] || "Description"}
              </h3>
            )}
            <p className="text-sm text-gray-600">{details?.desc}</p>
            {details?.more_info && (
              <h3 className="font-bold">
                {translations["more_info"] || "More info"}
              </h3>
            )}
            <p className="mb-2 text-gray-600">{details?.more_info}</p>
          </div>
          {/* <ProductTabs doc={{ desc: details?.desc, more_info: details?.more_info }} /> */}
          <ProductReviews
            rating={details?.rating_obj}
            avg_rate={details?.rating}
            total_rating={details.total_ratings}
            total_sold={details.total_sold}
            review={productReviews}
            translations={translations}
          />
          <div className="fixed md:relative left-0 right-0 bottom-0 p-4 md:p-0 md:mt-4 z-10 bg-white/50 backdrop-blur-xl shadow-sm md:shadow-none animated-slideUp flex justify-between items-center gap-4">
            <button
              onClick={() => router.push("/mycart")}
              className=" flex-1 border border-[#dddddd] p-2 md:p-4 text-[#6D401A] rounded-[10px] font-bold text-sm md:text-lg uppercase"
            >
              {translations["go_to_cart"] || "Go to cart"}
            </button>
            <button
              disabled={!details?.in_stk}
              onClick={() => {
                let cartItem = cart?.find(
                  (i) =>
                    (i.pdt_id === details.pdt_id &&
                      i.variant_id === variant?.variant_id) ||
                    ""
                );
                if (!cartItem) {
                  cartItem = {
                    pdt_id: details.pdt_id,
                    variant_id: variant?.variant_id || "",
                    quantity: 1,
                  };
                }
                return updateOrderSummary(cartItem,spl_prc_in_num);
              }}
              className=" flex-1 border p-2 text-white rounded-[10px] font-bold text-sm md:text-lg bg-[#6D401A] md:p-4 uppercase disabled:opacity-50"
            >
              {translations["buy_now"] || "Buy now"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
